import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/main.css';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import prawnImg from '../assets/prawn.jpg';
import vegImg from '../assets/veg.jpg';
import muttonImg from '../assets/mutton.jpg';
import chickenImg from '../assets/chicken.jpg';
import { FaPlus, FaMinus } from 'react-icons/fa'; // Add this for icons

const menuItems = [
	{
		name: 'Prawn Biryani',
		description: 'Royal prawns cooked with fragrant basmati rice.',
		image: prawnImg,
		price: 399,
	},
	{
		name: 'Veg Biryani',
		description: 'Flavor-packed biryani made with fresh vegetables.',
		image: vegImg,
		price: 249,
	},
	{
		name: 'Mutton Biryani',
		description: 'Slow-cooked mutton layered with aromatic rice.',
		image: muttonImg,
		price: 449,
	},
	{
		name: 'Chicken Dum Biryani',
		description: 'Authentic dum biryani with juicy chicken.',
		image: chickenImg,
		price: 299,
	},
];

const containerVariants = {
	visible: {
		transition: {
			staggerChildren: 0.18,
		},
	},
};

const itemVariants = {
	hidden: { opacity: 0, scale: 0.85, y: 60, rotate: -6 },
	visible: {
		opacity: 1,
		scale: 1,
		y: 0,
		rotate: 0,
		transition: {
			duration: 0.55,
			type: 'spring',
			bounce: 0.35,
		},
	},
	hover: {
		scale: 1.04,
		boxShadow: '0 8px 32px #ffd70055, 0 2px 16px #fff4',
		transition: { duration: 0.2 },
	},
};

// Wiggle animation for quantity select
const qtyVariants = {
	initial: { rotate: 0, backgroundColor: '#ffd70022' },
	animate: {
		rotate: [0, -10, 10, -8, 8, -4, 4, 0],
		backgroundColor: '#ffd70066',
		transition: { duration: 0.5, type: 'tween' }, // <-- use 'tween' here!
	},
};

const Menu = () => {
	const [selectedQty, setSelectedQty] = useState({});
	const { cartItems, addToCart } = useCart();
	const navigate = useNavigate();

	const handleQtyChange = (index, newQty) => {
		if (newQty < 1) return;
		setSelectedQty({ ...selectedQty, [index]: newQty });
	};

	const handleAddToCart = (item, index) => {
		const qty = selectedQty[index] || 1;
		addToCart(item, qty);
	};

	const goToCheckout = () => {
		navigate('/checkout');
	};

	return (
		<div
			className="royal-background"
			style={{ padding: '2rem', minHeight: '100vh' }}
		>
			<h2
				style={{
					color: '#ffd700',
					textAlign: 'center',
					marginBottom: '2rem',
				}}
			>
				Our Royal Menu
			</h2>
			<motion.div
				className="menu-grid"
				style={{
					display: 'grid',
					gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
					gap: '2rem',
				}}
				variants={containerVariants}
				initial="hidden"
				animate="visible"
			>
				<AnimatePresence>
					{menuItems.map((item, index) => {
						const qty = selectedQty[index] || 1;
						const totalPrice = qty * item.price;

						return (
							<motion.div
								key={item.name}
								variants={itemVariants}
								initial="hidden"
								animate="visible"
								whileHover="hover"
								exit={{ opacity: 0, scale: 0.8, y: 40 }}
								className="menu-card"
								style={{
									backgroundColor: '#001d4a',
									color: '#fff',
									padding: '1.5rem',
									borderRadius: '12px',
									boxShadow: '0 4px 15px rgba(0,0,0,0.5)',
									textAlign: 'center',
									position: 'relative',
									overflow: 'hidden',
								}}
							>
								<motion.img
									src={item.image}
									alt={item.name}
									style={{
										width: '100%',
										height: '200px',
										objectFit: 'cover',
										borderRadius: '10px',
										boxShadow: '0 2px 12px #ffd70022',
									}}
									initial={{ opacity: 0, scale: 1.1 }}
									animate={{ opacity: 1, scale: 1 }}
									transition={{ delay: 0.2, duration: 0.5 }}
								/>
								<h3 style={{ color: '#ffd700', marginTop: '1rem' }}>
									{item.name}
								</h3>
								<p>{item.description}</p>
								<motion.p
									style={{ fontWeight: 'bold', margin: '0.7rem 0' }}
									initial={{ opacity: 0, y: 10 }}
									animate={{ opacity: 1, y: 0 }}
									transition={{ delay: 0.3 }}
								>
									₹{item.price} × {qty} = ₹{totalPrice}
								</motion.p>

								{/* Quantity Stepper */}
								<div
									style={{
										marginTop: '1rem',
										display: 'flex',
										alignItems: 'center',
										justifyContent: 'center',
										gap: '0.7rem',
									}}
								>
									<button
										type="button"
										onClick={() => handleQtyChange(index, qty - 1)}
										style={{
											background: '#ffd700',
											border: 'none',
											borderRadius: '50%',
											width: '32px',
											height: '32px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: qty === 1 ? 'not-allowed' : 'pointer',
											opacity: qty === 1 ? 0.5 : 1,
											fontSize: '1.1rem',
										}}
										disabled={qty === 1}
										aria-label="Decrease quantity"
									>
										<FaMinus color="#001d4a" />
									</button>
									<span
										style={{
											minWidth: '2.2em',
											display: 'inline-block',
											fontWeight: 'bold',
											fontSize: '1.1rem',
											background: '#ffd70022',
											borderRadius: '6px',
											padding: '0.2em 0.8em',
										}}
									>
										{qty}
									</span>
									<button
										type="button"
										onClick={() => handleQtyChange(index, qty + 1)}
										style={{
											background: '#ffd700',
											border: 'none',
											borderRadius: '50%',
											width: '32px',
											height: '32px',
											display: 'flex',
											alignItems: 'center',
											justifyContent: 'center',
											cursor: 'pointer',
											fontSize: '1.1rem',
										}}
										aria-label="Increase quantity"
									>
										<FaPlus color="#001d4a" />
									</button>
								</div>

								<motion.button
									className="primary-btn"
									style={{
										marginTop: '1.2rem',
										fontWeight: 'bold',
										letterSpacing: '0.03em',
									}}
									whileHover={{
										scale: 1.07,
										boxShadow: '0 0 32px #ffd700cc',
									}}
									whileTap={{ scale: 0.96 }}
									onClick={() => handleAddToCart(item, index)}
								>
									Add to Cart
								</motion.button>
							</motion.div>
						);
					})}
				</AnimatePresence>
			</motion.div>

			{cartItems.length > 0 && (
				<motion.div
					style={{ textAlign: 'center', marginTop: '3rem' }}
					initial={{ opacity: 0, y: 30 }}
					animate={{ opacity: 1, y: 0 }}
				>
					<button
						className="primary-btn large"
						onClick={goToCheckout}
					>
						Proceed to Checkout ({cartItems.length} items)
					</button>
				</motion.div>
			)}
		</div>
	);
};

export default Menu;
